import clsx from 'clsx'
import React, { FC } from 'react'

import SvgCheck from '@Assets/SVG/Check'
import SvgClose from '@Assets/SVG/Close'
import SvgPlus from '@Assets/SVG/Plus'
import { ImageV4 } from '@Components/Image'
import { Maybe } from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'

import styles from './LineItemPersonalization.module.scss'
import stylesV2 from './LineItemPersonalizationV2.module.scss'

export interface LineItemPersonalizationProps {
  discountPrice?: string | Maybe<string>
  hideRemove?: boolean
  imageUrl?: string
  isFree?: boolean
  isPowerStep?: boolean
  onAdd?: () => void
  onEdit?: () => void
  onRemove?: () => void
  originalPrice?: string | Maybe<string>
  showAsLineItem?: boolean
  showCheckBox?: boolean
  text?: string
  title?: string
  v2?: boolean
}

export const LineItemPersonalization: FC<LineItemPersonalizationProps> = ({
  discountPrice,
  hideRemove = false,
  imageUrl,
  isFree,
  isPowerStep = false,
  onAdd,
  onEdit,
  onRemove,
  originalPrice,
  showAsLineItem = false,
  showCheckBox,
  text,
  title,
  v2,
}) => {
  const { t } = useTranslation()

  if (v2) {
    return (
      <div
        className={clsx(
          stylesV2.lineItemPersonalization,
          showAsLineItem && stylesV2.showAsLineItem,
          onAdd && stylesV2.addable,
          isPowerStep && stylesV2.powerStep,
        )}
        onClick={() => {
          if (onAdd) {
            onAdd()
          }
        }}
      >
        {imageUrl && (
          <div className={stylesV2.imageWrapper}>
            <ImageV4 height={showAsLineItem ? 90 : 70} src={imageUrl} width={showAsLineItem ? 90 : 70} />
          </div>
        )}
        <div className={clsx(stylesV2.contentWrapper, onAdd && stylesV2.toBasket, (onRemove || text) && stylesV2.inBasket)}>
          <div className={stylesV2.content}>
            {title}
            {text && (
              <div className={stylesV2.text}>
                {text}
                {onEdit && (
                  <>
                    {' - '}
                    <button className={stylesV2.edit} onClick={onEdit} type="button">
                      {t('cart:CHANGE')}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className={stylesV2.actions}>
            {originalPrice && (
              <span className={stylesV2.prices}>
                {!isFree && discountPrice && <p className={stylesV2.discount}>{`+ ${discountPrice}`}</p>}
                {isFree && <span className={stylesV2.discount}>{t('header:FREE_LABEL')}</span>}
                {originalPrice && (() => {
                  let prefix = ''
                  if (!discountPrice) {
                    prefix = onAdd ? '+' : ''
                  }
                  return (
                    <span className={clsx(stylesV2.price, (isFree || discountPrice) && stylesV2.discounted)}>{`${prefix} ${originalPrice}`}</span>
                  )
                })()}
              </span>
            )}

            {isPowerStep
              ? (
                <button aria-label="add Personalization" className={clsx(stylesV2.add, showCheckBox && stylesV2.success)} type="button">
                  {onAdd && <SvgPlus />}
                  {showCheckBox && <SvgCheck />}
                </button>
              )
              : showCheckBox && <div className={clsx(stylesV2.add, showCheckBox && stylesV2.success)}><SvgCheck /></div>}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={clsx(styles.lineItemPersonalization, onAdd && styles.addable)}
      onClick={() => {
        if (onAdd) {
          onAdd()
        }
      }}
    >
      {onRemove && (
        <button
          aria-label="remove item"
          className={clsx(styles.remove, hideRemove && styles.hideRemove, imageUrl && styles.withImage)}
          onClick={onRemove}
          type="button"
        >
          <SvgClose />
        </button>
      )}
      {imageUrl && (
        <div className={styles.image}>
          <ImageV4 className={styles.image} height={60} src={imageUrl} width={60} />
        </div>
      )}
      <div className={clsx(styles.description, onAdd && styles.toBasket, (onRemove || text) && styles.inBasket)}>
        <p className={clsx(styles.title, !text && !originalPrice && styles.fullWidth)}>{title}</p>
        {text && (
          <span className={styles.text}>
            <p>{text}</p>
            {onEdit && <p>-</p>}
            {onEdit && (
              <button className={styles.edit} onClick={onEdit} type="button">
                {t('cart:CHANGE')}
              </button>
            )}
          </span>
        )}
        {originalPrice && (
          <span className={styles.price}>
            {!isFree && discountPrice && <p className={styles.discountPrice}>{`+ ${discountPrice}`}</p>}
            {isFree && <p className={styles.discountPrice}>{t('header:FREE_LABEL')}</p>}
            {originalPrice
              && <p className={clsx(styles.originalPrice)}>{`${discountPrice ? '' : '+'} ${originalPrice}`}</p>}
          </span>
        )}
      </div>
      {onAdd
        && (
          <button aria-label="add Personalization" className={styles.add} type="button">
            <SvgPlus />
          </button>
        )}
      {showCheckBox && <span className={styles.check}><SvgCheck /></span>}
    </div>
  )
}

